import { WorkExperienceCompany } from '@applicator/shared'
import React from 'react'

import CustomCheckbox from '../CustomCheckbox'

import { useWorkExperienceHandlers } from './useWorkExperienceHandlers'
import WorkExperienceCompanyItem from './WorkExperienceCompanyItem'
import WorkExperiencePortals from './WorkExperiencePortals'
import ResumeSectionEmptyHint from './ResumeSectionEmptyHint'

export default function WorkExperienceEditor({ 
  data, 
  onChange,
  onAddCompanyRef,
  onAddRoleRef,
  highlightedRoleId,
  highlightedBulletId,
  highlightedCompanyId,
}: { 
  data: WorkExperienceCompany[]
  onChange: (data: WorkExperienceCompany[]) => void
  onAddCompanyRef?: (fn: () => void) => void
  onAddRoleRef?: (fn: () => void) => void
  highlightedRoleId?: string | null
  highlightedBulletId?: string | null
  highlightedCompanyId?: string | null
}) {
  const companies = Array.isArray(data) ? data : []
  
  const handlers = useWorkExperienceHandlers(companies, onChange, onAddCompanyRef, onAddRoleRef)
  
  const {
    editingCompanyId,
    editingCompanyDescId,
    editingRoleId,
    editingRoleLocationId,
    editingBulletId,
    editingBulletText,
    bulletTextareaRef,
    companyDescTextareaRef,
    datePickerState,
    datePickerRef,
    dateFieldRefs,
    editingCompanyName,
    editingCompanyDesc,
    editingRolePosition,
    editingRoleLocation,
    editingRoleStartDate,
    editingRoleEndDate,
    openTypeDropdown,
    typeDropdownRefs,
    draggedBulletId,
    dragOverIndex,
    draggedRoleId,
    draggedRoleCompanyId,
    dragOverRoleIndex,
    draggedCompanyId,
    dragOverCompanyIndex,
    hoveredBulletId,
    hoveredRoleId,
    hoveredCompanyId,
    hoveredCompanyDescId,
    employmentTypes,
    setEditingCompanyId,
    setEditingCompanyDescId,
    setEditingRoleId,
    setEditingRoleLocationId,
    setEditingBulletId,
    setEditingBulletText,
    setEditingCompanyName,
    setEditingCompanyDesc,
    setEditingRolePosition,
    setEditingRoleLocation,
    setHoveredBulletId,
    setHoveredRoleId,
    setHoveredCompanyId,
    setHoveredCompanyDescId,
    setDraggedBulletId,
    setDragOverIndex,
    setDraggedRoleId,
    setDraggedRoleCompanyId,
    setDragOverRoleIndex,
    setDraggedCompanyId,
    setDragOverCompanyIndex,
    clearRoleDrag,
    clearCompanyDrag,
    handleReorderRoles,
    handleReorderCompanies,
    handleStartEditCompany,
    handleSaveCompany,
    handleStartEditCompanyDesc,
    handleSaveCompanyDesc,
    handleDeleteCompany,
    handleToggleCompany,
    handleToggleCompanyDescription,
    handleAddRole,
    handleCompanyDescChange,
    adjustTextareaHeight,
    handleDateChange,
    handleDatePickerClose,
    handleUpdateEmploymentType,
    handleStartEditRole,
    handleSaveRole,
    handleStartEditRoleLocation,
    handleSaveRoleLocation,
    handleDeleteRole,
    handleTogglePosition,
    handleToggleLocation,
    handleAddBullet,
    handleToggleBullet,
    handleDeleteBullet,
    handleReorderBullets,
    handleStartEditBullet,
    handleSaveBullet,
    handleCancelEditBullet,
    handleOpenTypeDropdown,
    handleStartEditRoleStartDate,
    handleStartEditRoleEndDate,
    handleBulletTextChange,
  } = handlers

  // Helper function to render checkbox using CustomCheckbox component (same as in tables)
  const renderCheckbox = (checked: boolean, onChange: () => void, size: 'sm' | 'md' = 'md') => {
    return (
      <CustomCheckbox
        checked={checked}
        onChange={onChange}
        size={size}
        className="flex-shrink-0"
      />
    )
  }

  // Prepare role props to pass to WorkExperienceRoleItem
  const roleProps = {
    editingRoleId,
    editingRoleLocationId,
    editingRolePosition,
    editingRoleLocation,
    editingBulletId,
    editingBulletText,
    hoveredRoleId,
    hoveredBulletId,
    draggedBulletId,
    dragOverIndex,
    bulletTextareaRef,
    typeDropdownRefs,
    dateFieldRefs,
    onStartEditRole: handleStartEditRole,
    onSaveRole: handleSaveRole,
    onStartEditRoleLocation: handleStartEditRoleLocation,
    onSaveRoleLocation: handleSaveRoleLocation,
    onDeleteRole: handleDeleteRole,
    onTogglePosition: handleTogglePosition,
    onToggleLocation: handleToggleLocation,
    onAddBullet: handleAddBullet,
    onToggleBullet: handleToggleBullet,
    onDeleteBullet: handleDeleteBullet,
    onReorderBullets: handleReorderBullets,
    onStartEditBullet: handleStartEditBullet,
    onSaveBullet: handleSaveBullet,
    onCancelEditBullet: handleCancelEditBullet,
    onOpenTypeDropdown: handleOpenTypeDropdown,
    onStartEditRoleStartDate: handleStartEditRoleStartDate,
    onStartEditRoleEndDate: handleStartEditRoleEndDate,
    setHoveredRoleId,
    setHoveredBulletId,
    setHoveredCompanyId,
    setDraggedBulletId,
    setDragOverIndex,
    setEditingRolePosition,
    setEditingRoleLocation,
    setEditingRoleId,
    setEditingRoleLocationId,
    setEditingBulletId,
    setEditingBulletText,
    adjustTextareaHeight,
    handleBulletTextChange,
    renderCheckbox,
  }

  return (
    <div className="space-y-4">
      {companies.length === 0 && <ResumeSectionEmptyHint sectionType="work_experience" />}

      {companies.map((company, companyIndex) => (
        <WorkExperienceCompanyItem
          key={`${company.id}-${companyIndex}`}
          company={company}
          companyIndex={companyIndex}
          editingCompanyId={editingCompanyId}
          editingCompanyDescId={editingCompanyDescId}
          editingCompanyName={editingCompanyName}
          editingCompanyDesc={editingCompanyDesc}
          hoveredCompanyId={hoveredCompanyId}
          hoveredCompanyDescId={hoveredCompanyDescId}
          companyDescTextareaRef={companyDescTextareaRef}
          highlightedRoleId={highlightedRoleId}
          highlightedBulletId={highlightedBulletId}
          highlightedCompanyId={highlightedCompanyId}
          onStartEditCompany={handleStartEditCompany}
          onSaveCompany={handleSaveCompany}
          onStartEditCompanyDesc={handleStartEditCompanyDesc}
          onSaveCompanyDesc={handleSaveCompanyDesc}
          onDeleteCompany={handleDeleteCompany}
          onToggleCompany={handleToggleCompany}
          onToggleCompanyDescription={handleToggleCompanyDescription}
          onAddRole={handleAddRole}
          onCompanyDescChange={handleCompanyDescChange}
          adjustTextareaHeight={adjustTextareaHeight}
          renderCheckbox={renderCheckbox}
          setHoveredCompanyId={setHoveredCompanyId}
          setHoveredCompanyDescId={setHoveredCompanyDescId}
          setEditingCompanyName={setEditingCompanyName}
          setEditingCompanyId={setEditingCompanyId}
          setEditingCompanyDescId={setEditingCompanyDescId}
          setEditingCompanyDesc={setEditingCompanyDesc}
          roleProps={roleProps}
          draggedRoleId={draggedRoleId}
          draggedRoleCompanyId={draggedRoleCompanyId}
          dragOverRoleIndex={dragOverRoleIndex}
          setDraggedRoleId={setDraggedRoleId}
          setDraggedRoleCompanyId={setDraggedRoleCompanyId}
          setDragOverRoleIndex={setDragOverRoleIndex}
          onReorderRoles={handleReorderRoles}
          clearRoleDrag={clearRoleDrag}
          draggedCompanyId={draggedCompanyId}
          dragOverCompanyIndex={dragOverCompanyIndex}
          setDraggedCompanyId={setDraggedCompanyId}
          setDragOverCompanyIndex={setDragOverCompanyIndex}
          onReorderCompanies={handleReorderCompanies}
          clearCompanyDrag={clearCompanyDrag}
        />
      ))}

      <WorkExperiencePortals
        companies={companies}
        datePickerState={datePickerState}
        datePickerRef={datePickerRef}
        editingRoleStartDate={editingRoleStartDate}
        editingRoleEndDate={editingRoleEndDate}
        openTypeDropdown={openTypeDropdown}
        typeDropdownRefs={typeDropdownRefs}
        employmentTypes={employmentTypes}
        onDateChange={handleDateChange}
        onDatePickerClose={handleDatePickerClose}
        onUpdateEmploymentType={handleUpdateEmploymentType}
      />
    </div>
  )
}